import { useState, lazy, Suspense } from "react";

import Spinner from "common/components/Spinner";

import Markdown from "client/common/components/content/Markdown";
import Accordion from "client/common/components/interactive/Accordion";
import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import TextButton from "client/common/components/buttons/TextButton";

import { IProductLineDetails } from "../types";

import styles from "../styles/ProductLineAccordion.module.less";

// Lazily import since we're using markdown and tons of icons here to not block the above the fold
// item selections and content
const ProductLineQuickFacts = lazy(() => import("./ProductLineQuickFacts"));

type ProductLineAccordionProps = { productLine: IProductLineDetails };

const ProductLineUsage = ({ productLine }: ProductLineAccordionProps) => {
  return (
    <div className={styles.usage}>
      {productLine.usage_details && <Markdown size="s" markdown={productLine.usage_details} />}
      {productLine.prescribing_information_url && (
        <TextButton
          icon="right"
          className={styles.link}
          href={productLine.prescribing_information_url}
        >
          View full prescribing information
        </TextButton>
      )}
    </div>
  );
};

const ProductLineSafetyInformation = ({ productLine }: ProductLineAccordionProps) => {
  const [isSafetyModalOpen, setIsSafetyModalOpen] = useState(false);

  return (
    <div className={styles.safety}>
      {productLine.safety_overview && <Markdown size="s" markdown={productLine.safety_overview} />}
      {(productLine.safety_information || productLine.msds_url) && (
        <div className={styles.links}>
          {productLine.safety_information && (
            <TextButton
              icon="right"
              className={styles.link}
              type="button"
              onClick={() => setIsSafetyModalOpen(true)}
            >
              View all safety information
            </TextButton>
          )}
          {productLine.msds_url && (
            <TextButton icon="right" className={styles.link} href={productLine.msds_url}>
              MSDS sheet
            </TextButton>
          )}
        </div>
      )}
      <Modal visible={isSafetyModalOpen} onClose={() => setIsSafetyModalOpen(false)}>
        <ModalLayout>
          <ModalLayout.Header
            title="Safety Information"
            onClose={() => setIsSafetyModalOpen(false)}
          />
          <ModalLayout.Content>
            <Markdown className={styles.modal} markdown={productLine.safety_information} />
          </ModalLayout.Content>
        </ModalLayout>
      </Modal>
    </div>
  );
};

const ProductLineGuaranteedAnalysis = ({ productLine }: ProductLineAccordionProps) => {
  return (
    <div className={styles.analysis}>
      <div className={styles.items}>
        {productLine.guaranteed_analysis_items.map((item) => (
          <div className={styles.item} key={item.id}>
            <div className={styles.label}>{item.nutrient}</div>
            <div className={styles.value}>
              {item.amount} {item.minmax.toLocaleLowerCase()}
            </div>
          </div>
        ))}
      </div>
      {productLine.guaranteed_analysis_fine_print && (
        <div className={styles.fine}>{productLine.guaranteed_analysis_fine_print}</div>
      )}
    </div>
  );
};

const ProductLineAccordion = ({ productLine }: ProductLineAccordionProps) => {
  const items = [
    {
      key: "overview",
      label: `Meet ${productLine.brand_name}`,
      content: (
        <Suspense fallback={<Spinner />}>
          <ProductLineQuickFacts productLine={productLine} />
        </Suspense>
      ),
      isVisible: !!productLine.facts.length,
    },
    {
      key: "description",
      label: "Description",
      content: <Markdown size="s" markdown={productLine.description} />,
      isVisible: !!productLine.description,
    },
    {
      key: "feeding_guide",
      label: "Feeding Guide",
      content: <Markdown size="s" markdown={productLine.feeding_guide} />,
      isVisible: !!productLine.feeding_guide,
    },
    {
      key: "how",
      label: "How to use",
      content: <ProductLineUsage productLine={productLine} />,
      isVisible: !!productLine.usage_details || !!productLine.prescribing_information_url,
    },
    {
      key: "what",
      label: "What's in it?",
      content: (
        <Markdown
          size="s"
          markdown={productLine.ingredients?.map((ing) => ing.display).join(", ")}
        />
      ),
      isVisible: !!productLine.ingredients.length,
    },
    {
      key: "analysis",
      label: "Guaranteed Analysis",
      content: <ProductLineGuaranteedAnalysis productLine={productLine} />,
      isVisible: !!productLine.guaranteed_analysis_items.length,
    },
    {
      key: "safety",
      label: "Safety information",
      content: <ProductLineSafetyInformation productLine={productLine} />,
      isVisible:
        !!productLine.safety_overview || !!productLine.safety_information || !!productLine.msds_url,
    },
  ].filter(({ isVisible }) => isVisible);

  if (!items.length) {
    return null;
  }

  return <Accordion.Single items={items} initialOpenKey="overview" />;
};

export default ProductLineAccordion;
